<template>
    <div v-loading="isBusy">
        <div class="row">
            <div class="col-sm-8">
                <h1>
                    Payments
                    <span v-if="isSelectingDate">
                        <select :value="from.format('YYYY-MM-DD')" @change="dateSelected" style="font-size:0.8em">
                            <option v-for="d in dates" :key="`select-month-${d.format('YYYY-MM')}`"
                                    :value="d.format('YYYY-MM-DD')">{{ d.format('MMMM YYYY') }}</option>
                        </select>
                    </span>
                    <span v-else @click="isSelectingDate = true" style="border-bottom:1px dashed black;cursor:pointer;">
                        {{ from.format('MMMM YYYY') }}
                    </span>
                </h1>
            </div>
            <div class="col-sm-2">
                <span class="is-aligned-right">
                    Type:<br>
                    <select v-model="type" @change="reloadData" class="form-control" style="display:inline;width:auto;">
                        <option v-for="(t, key) in typeOptions"
                                :key="`payments-type-opt-${key}`"
                                :value="key"
                                :label="t"></option>
                    </select>
                </span>
            </div>
            <div class="col-sm-2">
                <span class="is-aligned-right">
                    Sort by:<br>
                    <select v-model="sort" @change="reloadData" class="form-control" style="display:inline;width:auto;">
                        <option v-for="(s, key) in sortOptions"
                                :key="`payments-sort-opt-${key}`"
                                :value="key"
                                :label="s"></option>
                    </select>
                </span>
            </div>
        </div>

        <div class="row" v-if="unlinkedPaymentsCount > 0">
            <div class="col-sm-12">
                <div class="alert alert-danger">
                    You have {{ unlinkedPaymentsCount }} orphaned payments. <a
                    :href="url('admin/settings/orphaned-payments')">Assign them on the orphaned payments page.</a>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-12">
                <div>
                    <table class="table table-striped table-hover table-sticky-header">
                        <thead>
                            <tr>
                                <th>Employer</th>
                                <th>Budget</th>
                                <th>Billable Amount</th>
                                <th>Current cost</th>
                                <th>Subscription</th>
                                <th>Net Invoice</th>
                                <th>Status</th>
                                <th>Detail</th>
                            </tr>
                        </thead>

                        <PaymentsDrilldown v-for="(row, index) in rows"
                                           :key="`payments-drilldown-${from.format('YYYY-MM')}-${index}`"
                                           :index="index"
                                           @open-net-invoice-dialog="openNetInvoiceDialog"
                                           @open-payment-status-dialog="openPaymentStatusDialog"
                                           :row="row"></PaymentsDrilldown>

                    </table>
                </div>
            </div>
        </div>

        <el-dialog
            title="Net Invoice"
            :visible.sync="netInvoiceDialog.isVisible"
            width="30%"
            :close-on-click-modal="false"
            :before-close="closeNetInvoiceDialog">
            <div>
                <el-input type="text" v-model="netInvoiceDialog.value"></el-input>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="closeNetInvoiceDialog">Cancel</el-button>
                <el-button type="primary" @click="saveNetInvoice"><i class="fa fa-pulse fa-spinner" v-if="netInvoiceDialog.isBusy"></i> Save</el-button>
            </span>
        </el-dialog>

        <el-dialog
            title="Payment Status"
            :visible.sync="statusDialog.isVisible"
            width="30%"
            :close-on-click-modal="false"
            :before-close="closePaymentStatusDialog">
            <div>
                <el-select v-model="statusDialog.value" style="width:100%">
                    <el-option v-for="op in statusOptions" :label="op" :value="op" :key="`status-${op}`"></el-option>
                </el-select>
                <el-date-picker v-model="statusDialog.date"
                                v-if="statusDialog.value === 'Invoiced' || statusDialog.value === 'Paid'"
                                style="width:100%;margin-top:5px;"
                                type="date"
                                format="yyyy-MM-dd"
                                value-format="yyyy-MM-dd"
                                placeholder="Pick a day"></el-date-picker>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="closePaymentStatusDialog">Cancel</el-button>
                <el-button type="primary" @click="savePaymentStatus"><i class="fa fa-pulse fa-spinner" v-if="statusDialog.isBusy"></i> Save</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import PaymentsDrilldown from './payments-drilldown';

export default {
    components: { PaymentsDrilldown },
    props: {
        sortOptions: { type: Object, required: true },
        typeOptions: { type: Object, required: true },
    },
    data() {
        let dates = _.map(_.range(0, 16), (diff) => {
            return moment().startOf('month').add(1, 'months').subtract(diff, 'months');
        });

        // Parse URL params up-front so `from`/`type`/`sort` are correct on first
        // render. Doing it here (rather than reassigning in mounted) means the
        // `from` watcher does not fire during init, giving a single initial load.
        let searchParams = new URLSearchParams(window.location.search.substring(1));
        let from = searchParams.get('from') !== null
            ? moment(searchParams.get('from'), 'YYYY-MM-DD')
            : moment().startOf('month');
        let typeParam = searchParams.get('type');
        let type = (typeParam !== null && Object.keys(this.typeOptions).includes(typeParam)) ? typeParam : '';
        let sortParam = searchParams.get('sort');
        let sort = (sortParam !== null && Object.keys(this.sortOptions).includes(sortParam)) ? sortParam : 'name';

        return {
            from: from,
            type: type,
            sort: sort,
            dates: dates,
            isSelectingDate: false,
            isBusy: false,
            rows: [],
            unlinkedPaymentsCount: 0,
            netInvoiceDialog: {
                value: '',
                period: null,
                employer: null,
                index: null,
                isVisible: false,
                isBusy: false,
            },
            statusOptions: ['Assumed', 'Invoiced', 'Paid', 'Not Advertising'],
            statusDialog: {
                value: 'Assumed',
                date: '',
                period: null,
                employer: null,
                index: null,
                isVisible: false,
                isBusy: false,
            },
        };
    },
    mounted() {
        this.reloadData();
    },
    watch: {
        // Reload whenever the month changes, regardless of how `from` was set.
        // Decouples loading from the native <select> change/input events, which
        // could previously fail to fire (re-selecting the same month, input vs
        // change ordering, the select being torn down mid-event).
        from() {
            this.reloadData();
        },
    },
    methods: {
        dateSelected(event) {
            this.from = moment(event.target.value, 'YYYY-MM-DD');
            this.isSelectingDate = false;
        },
        openNetInvoiceDialog(data) {
            data['isVisible'] = true;
            data['isBusy'] = false;
            this.netInvoiceDialog = data;
        },
        closeNetInvoiceDialog() {
            this.netInvoiceDialog.isVisible = false;
            this.netInvoiceDialog.isBusy = false;
            this.netInvoiceDialog.value = '';
            this.netInvoiceDialog.period = null;
            this.netInvoiceDialog.employer = null;
            this.netInvoiceDialog.index = null;
        },
        saveNetInvoice() {
            this.netInvoiceDialog.isBusy = true;
            // Capture dialog state before the async call: closeNetInvoiceDialog()
            // nulls these out and can run before the POST resolves.
            const { employer, period, value, index } = this.netInvoiceDialog;
            return axios.post(url(`ajax/admin/employer/${employer}/payments/net-invoice`), {
                pk: period,
                value: value,
            }).then(response => {
                if (this.rows[index]) this.rows[index].net_invoice = value;
            }).finally(() => {
                this.closeNetInvoiceDialog();
            });
        },
        openPaymentStatusDialog(data) {
            data['date'] = '';
            data['isVisible'] = true;
            data['isBusy'] = false;
            this.statusDialog = data;
        },
        closePaymentStatusDialog() {
            this.statusDialog.isVisible = false;
            this.statusDialog.isBusy = false;
            this.statusDialog.value = 'Assumed';
            this.statusDialog.date = '';
            this.statusDialog.period = null;
            this.statusDialog.employer = null;
            this.statusDialog.index = null;
        },
        savePaymentStatus() {
            this.statusDialog.isBusy = true;

            // Capture dialog state before the async call (see saveNetInvoice).
            const { employer, period, index, value, date } = this.statusDialog;

            const params = {
                pk: period,
                value: { state: value },
            };

            if (value === 'Paid') params['value']['paid_date'] = date;
            else if (value === 'Invoiced') params['value']['invoiced_date'] = date;

            return axios.post(url(`ajax/admin/employer/${employer}/payments/paid-status`), params)
                        .then(response => {
                            let status = value;
                            if (value === 'Invoiced' || value === 'Paid') {
                                status += ', ' + moment(date).format('ddd MMMM D YYYY');
                            }
                            if (this.rows[index]) this.rows[index].paid_status_text = status;
                        })
                        .finally(() => {
                            this.closePaymentStatusDialog();
                        });
        },
        reloadData() {
            this.isBusy = true;

            let queryString = new URLSearchParams({
                from: this.from.format('YYYY-MM-DD'),
                type: this.type,
                sort: this.sort,
            }).toString();
            history.pushState(history.state, document.title, url(`admin/payments/?${queryString}`));

            return axios.get(url(`admin/payments/get-data`), {
                params: {
                    from: this.from.format('YYYY-MM-DD'),
                    type: this.type,
                    sortBy: this.sort,
                },
            }).then(response => {
                this.unlinkedPaymentsCount = response.data.unlinked_payments_count;
                this.rows = response.data.rows || [];
            }).finally(() => {
                this.isBusy = false;
            });
        },
    },
};
</script>

<style scoped>
/* Unfortunately this sticky-footer solution is not compatible with "table-responsive" ("overflow-x: auto" on a parent element disables the sticky header) */
.table-sticky-header {
    position: relative;
    border-collapse: collapse;
    background: #fff;
}
.table-sticky-header thead th {
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    z-index: 10;
    background: #F4F6F6;
}
</style>
